// Commands (IMPLEMENTATION §2.3). Simulation.apply(cmd) validates and applies a command synchronously at
// dispatch (phase 0 of the next tick), logs it as {t: state.tick, seq, cmd} and returns a CmdResult (the UI
// uses the reason for snap-back text). A rejected command changes nothing.
import type { Cell, Int } from './core/types.ts';
import type { ExpansionId, FamId, ShopId } from './gen/ids.ts';

/** Drag / tap-select source. */
export type Src =
  | { k: 'cell'; x: Int; y: Int; id: Int }
  | { k: 'slot'; bid: Int; slot: Int; idx: Int; id: Int }
  | { k: 'shop'; item: ShopId }
  /** v0.2 supply tray. */
  | { k: 'tray'; idx: Int; id: Int };

/** Drop target. `sell` is the Build-mode Sell tool; `gunSlot` the Gallery card mount (v0.2). */
export type Dst =
  | { k: 'cell'; x: Int; y: Int }
  | { k: 'counter' }
  | { k: 'sell' }
  | { k: 'gunSlot'; bid: Int };

export type DbgOp = 'cash' | 'spawn' | 'level' | 'unlockAll' | 'goal';

export type Command =
  | { c: 'hit'; x: Int; y: Int }
  | { c: 'hold'; x: Int; y: Int }
  | { c: 'holdEnd' }
  | { c: 'grab'; id: Int }
  | { c: 'release' }
  | { c: 'drop'; from: Src; to: Dst; build: boolean }
  | { c: 'tapGallery'; bid: Int }
  | { c: 'undoSell'; token: Int }
  | { c: 'crateSellAll'; bid: Int }
  | { c: 'quickMerge'; id: Int }
  | { c: 'mergeAll' }
  | { c: 'rotate'; bid: Int }
  | { c: 'pause'; bid: Int; on: boolean }
  | { c: 'setTarget'; bid: Int; tier: Int }
  /** v0.2 */
  | { c: 'setMode'; bid: Int; mode: string }
  | { c: 'sellBuilding'; bid: Int }
  | { c: 'cancelGhost'; bid: Int }
  | { c: 'paintChutes'; path: Cell[] }
  | { c: 'eraseChutes'; cells: Cell[] }
  | { c: 'undoBuild' }
  | { c: 'expand'; id: ExpansionId }
  /**
   * Debug (DebugPanel / tests). cash: add `a` (never below 0) · spawn: `fam` T`tier` at (x,y), booked as
   * granted · level: set the building at (x,y) to level `a` · unlockAll: every active unlock with its
   * effects · goal: every active unlock up to table index `a` (goal jump), with effects.
   */
  | { c: 'dbg'; op: DbgOp; a?: Int; fam?: FamId; tier?: Int; x?: Int; y?: Int };

export type CommandKind = Command['c'];

/**
 * Reject reasons (§2.5 plus the extra copy keys). Each has a copy.json `reason.<key>` text of ≤ 4 words
 * (tests/sim/commands.test.ts keeps the two lists equal).
 */
export const REJECT_REASONS = [
  'stale',
  'pinned',
  'pinnedGun',
  'occupied',
  'max',
  'notAccepted',
  'needsTier',
  'noCash',
  'locked',
  'notUnlocked',
  'buildOnly',
  'full',
  'noRoom',
  'wired',
  'mounted',
  'spent',
  'cooldown',
] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];

export type CmdResult = { ok: true } | { ok: false; reason: RejectReason };

export function ok(): CmdResult {
  return { ok: true };
}

export function reject(reason: RejectReason): CmdResult {
  return { ok: false, reason };
}

/** One command-log row (§0.1): replay applies every row with t = N before running tick N + 1. */
export interface LogEntry {
  t: Int;
  seq: Int;
  cmd: Command;
}
